export type OrderStatus =
  | 'In Progress'
  | 'Pending'
  | 'Cancelled'
  | 'Accepted'
  | 'Completed'
  | 'On Hold'

export type PaymentStatus = 'Unpaid' | 'Partial' | 'Settled'

export type LabOrderListItem = {
  id: string
  _id: string
  case: string
  labName: string
  clinic: string
  orderStatus: OrderStatus
  payment: PaymentStatus
  orderDate?: string
  dueDate?: string
  finalTotal: string
  totalReceived?: string
  balanceDue?: string
  balanceDueAmount?: number
  itemCount?: number
  patient?: string
  patientMeta?: string
  treatmentId?: string
  isLocalTracking?: boolean
  trackingMode?: 'platform' | 'local'
  /** Sent to a registered lab that has not accepted or declined it yet */
  awaitingAcceptance?: boolean
  /** Self-tracked order the clinic hasn't priced yet */
  pricePending?: boolean
  /** The order's partner row and registered lab (price list to load when editing) */
  labPartnerId?: string
  labId?: string
  /** The clinic's case ID (YYMM-CNN); empty on orders saved before case IDs */
  clinicCaseId?: string
  /** The lab's own order number */
  labOrderNumber?: string
  orderDateIso?: string
  priority?: 'Normal' | 'Urgent'
  modelledByPartnerId?: string
  modelledByName?: string
  dentistId?: string
  dentistName?: string
  patientInformedAt?: string | null
  labInvoiceNo?: string
  clinicUpdatedAt?: string | null
  stageId?: string
  stageName?: string
  /** The treatment stage the order was made from was deleted from the sheet */
  stageRemoved?: boolean
}

/** "2610-101", or "#12" on orders without a case ID */
export function caseLabel(order: { clinicCaseId?: string; id?: string }) {
  return order.clinicCaseId || (order.id ? `#${order.id}` : '')
}

export const URGENT_BADGE = 'bg-red-100 text-red-700 ring-1 ring-red-200 dark:bg-red-950/50 dark:text-red-300'

export type InvoiceLineItem = {
  /** Clinic price list entry the line came from (hand-added labs) */
  localItemId?: string
  /** Registered lab's price list row */
  labItemId?: string
  /** Care32 catalogue item and its names at order time */
  catalogItemId?: string
  catalogName?: string
  categoryId?: string
  categoryName?: string
  caseCode?: number
  pricingMethod?: 'flat' | 'base_plus_tooth'
  item: string
  description?: string
  notes?: string
  quantity?: number
  unitPrice?: number
  basePrice?: number
  toothPrice?: number
  includedTeeth?: number
  teeth?: number
  /** unitPrice x quantity */
  price: number
  lineDiscount: number
  netPrice: number
}

export type PaymentRecord = {
  date: string
  method: string
  amount: number
  discount: number
  total: number
  recordedBy: string
}

export type WorkflowStep = {
  title: string
  timestamp?: string
  estimate?: string
  /** Optional steps (e.g. Try-in) are marked skipped when an order jumps past them */
  optional?: boolean
  state: 'completed' | 'current' | 'upcoming' | 'skipped'
}

export type StageLogEntry = {
  date: string
  stage: string
  /** Only set on entries logged before stages replaced the status picker */
  status?: 'Completed' | 'In Progress' | 'Pending'
  by: string
  addedBy: string
  notes: string
}

export type LabOrderPhoto = {
  id: string
  /** Short-lived signed URL; null when the file couldn't be signed */
  url: string | null
  /** Clinic photos show the case; lab photos show the finished work */
  uploadedBy: 'clinic' | 'lab'
  uploadedByName: string
  createdAt: string
  /** Decided by the backend for this viewer — don't re-derive it here */
  canDelete: boolean
}

export type LabOrderDetail = LabOrderListItem & {
  /** Stages the clinic may log now, decided by the server */
  allowedStages?: string[]
  clinicLocation: string
  patient: string
  patientMeta: string
  caseTitle: string
  caseMeta: string
  caseDetail: string
  toothNos: string[]
  receivedBy: string
  receivedType: string
  deliveryAddress: string
  contact: string
  addedBy: string
  paymentLabel: string
  netTotal: number
  generalDiscount: number
  deliveryCharge: number
  finalTotalAmount: number
  totalReceived: number
  balanceDue: number
  invoiceItems: InvoiceLineItem[]
  payments: PaymentRecord[]
  workflow: WorkflowStep[]
  stageLog: StageLogEntry[]
  /** Missing on older backends — treat as [] */
  photos?: LabOrderPhoto[]
  caseCategoryId?: string
  shade?: string
  shadeMix?: string
  /** What the clinic may change now, decided by the server */
  editable?: { fields: string[]; reason: string }
  /** The lab's email; empty = "Email to lab" can't work */
  labEmail?: string
}

export const orderStatusStyles: Record<OrderStatus, string> = {
  Pending: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  'In Progress': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  Accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  Completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  Cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  'On Hold': 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
}

export const paymentStatusStyles: Record<PaymentStatus, string> = {
  Unpaid: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  Partial: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Settled: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
}

export const stageStatusStyles: Record<NonNullable<StageLogEntry['status']>, string> = {
  Completed: 'bg-emerald-100 text-emerald-800',
  'In Progress': 'bg-amber-100 text-amber-800',
  Pending: 'bg-slate-100 text-slate-700',
}

export function formatLkr(amount: number) {
  return `LKR ${(Number(amount) || 0).toLocaleString('en-LK')}`
}

export function resolveOrderKey(order: { _id?: string; id?: string }) {
  const mongoId = order._id != null ? String(order._id).trim() : ''
  if (mongoId) return mongoId
  return String(order.id || '').trim()
}

export function orderMatchesKey(
  order: { _id?: string; id?: string } | null | undefined,
  key: string | null | undefined,
) {
  if (!order || key == null) return false
  const normalizedKey = String(key).trim()
  if (!normalizedKey) return false
  const mongoId = order._id != null ? String(order._id).trim() : ''
  const orderNumber = order.id != null ? String(order.id).trim() : ''
  return mongoId === normalizedKey || orderNumber === normalizedKey
}
